import { useRef, useState, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import { useEditorial } from "@/hooks/useEditorial";

export default function GlowEditSection() {
  const { posts: glowPosts, loading } = useEditorial();
  const trackRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef({ x: 0, scrollLeft: 0 });

  const updateScrollState = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
  }, []);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollLeft = 0;
    updateScrollState();
    el.addEventListener("scroll", updateScrollState, { passive: true });
    const ro = new ResizeObserver(updateScrollState);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", updateScrollState);
      ro.disconnect();
    };
  }, [updateScrollState]);

  const getCardWidth = () => {
    const el = trackRef.current;
    if (!el) return 0;
    const firstCard = el.querySelector<HTMLElement>("[data-glow-card]");
    return firstCard ? firstCard.offsetWidth + 24 : 0;
  };

  const scrollBy = (dir: "left" | "right") => {
    const el = trackRef.current;
    if (!el) return;
    const step = getCardWidth() * 2;
    el.scrollBy({ left: dir === "left" ? -step : step, behavior: "smooth" });
  };

  const onMouseDown = (e: React.MouseEvent) => {
    const el = trackRef.current;
    if (!el) return;
    setIsDragging(true);
    dragStart.current = { x: e.pageX, scrollLeft: el.scrollLeft };
    el.style.cursor = "grabbing";
  };

  const onMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    const el = trackRef.current;
    if (!el) return;
    el.scrollLeft = dragStart.current.scrollLeft - (e.pageX - dragStart.current.x);
  };

  const onMouseUp = () => {
    setIsDragging(false);
    if (trackRef.current) trackRef.current.style.cursor = "grab";
  };

  if (loading || glowPosts.length === 0) {
    return (
      <section
        className="py-20 lg:py-28 px-6 lg:px-16 bg-moonlight flex items-center justify-center"
        style={{ fontFamily: "'Jost', sans-serif" }}
      >
        <p className="text-sm tracking-widest uppercase text-midnight/40 animate-pulse">Loading...</p>
      </section>
    );
  }

  return (
    <section
      className="py-20 lg:py-28 px-6 lg:px-16 bg-moonlight"
      style={{ fontFamily: "'Jost', sans-serif" }}
    >
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-5 mb-12 max-w-6xl mx-auto">
        <div>
          <p
            className="text-[10px] tracking-ultra-wide uppercase text-midnight/40 mb-3"
            style={{ letterSpacing: "0.3em" }}
          >
            Editorial
          </p>
          <h2
            className="text-3xl md:text-4xl font-light italic text-midnight"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            The Glow Edit
          </h2>
          <p className="text-sm text-midnight/50 mt-2 max-w-sm">
            Style stories, fabric guides, and the women who illuminate every room they enter.
          </p>
        </div>
        <Link
          to={`/editorial/${glowPosts[0].slug}`}
          className="text-xs tracking-widest uppercase text-midnight/60 hover:text-midnight transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer"
        >
          Read All <i className="ri-arrow-right-line" />
        </Link>
      </div>

      {/* Carousel */}
      <div className="max-w-6xl mx-auto relative group/glow-carousel">
        {/* Left Arrow */}
        <button
          onClick={() => scrollBy("left")}
          className={`absolute left-0 top-[38%] -translate-y-1/2 -translate-x-5 z-10
            w-10 h-10 flex items-center justify-center bg-moonlight border border-pearl
            text-midnight/60 hover:text-midnight hover:border-midnight
            transition-all duration-200 cursor-pointer whitespace-nowrap
            ${canScrollLeft ? "opacity-100" : "opacity-0 pointer-events-none"}`}
          aria-label="Scroll left"
        >
          <i className="ri-arrow-left-s-line text-lg" />
        </button>

        {/* Right Arrow */}
        <button
          onClick={() => scrollBy("right")}
          className={`absolute right-0 top-[38%] -translate-y-1/2 translate-x-5 z-10
            w-10 h-10 flex items-center justify-center bg-moonlight border border-pearl
            text-midnight/60 hover:text-midnight hover:border-midnight
            transition-all duration-200 cursor-pointer whitespace-nowrap
            ${canScrollRight ? "opacity-100" : "opacity-0 pointer-events-none"}`}
          aria-label="Scroll right"
        >
          <i className="ri-arrow-right-s-line text-lg" />
        </button>

        {/* Left fade edge */}
        <div
          className={`pointer-events-none absolute left-0 top-0 h-full w-16 z-[5]
            bg-gradient-to-r from-moonlight to-transparent transition-opacity duration-300
            ${canScrollLeft ? "opacity-100" : "opacity-0"}`}
        />
        {/* Right fade edge */}
        <div
          className={`pointer-events-none absolute right-0 top-0 h-full w-16 z-[5]
            bg-gradient-to-l from-moonlight to-transparent transition-opacity duration-300
            ${canScrollRight ? "opacity-100" : "opacity-0"}`}
        />

        {/* Track */}
        <div
          ref={trackRef}
          className="flex gap-6 overflow-x-auto scroll-smooth select-none hide-scrollbar"
          style={{ cursor: "grab" }}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseUp}
        >
          {glowPosts.map((post) => (
            <Link
              key={post.id}
              to={`/editorial/${post.slug}`}
              data-glow-card
              className="group flex-none w-[calc(33.333%-16px)] min-w-[280px] block cursor-pointer"
              draggable={false}
            >
              {/* Image */}
              <div className="relative w-full overflow-hidden bg-pearl/20" style={{ aspectRatio: "16/11" }}>
                <img
                  src={post.coverImage}
                  alt={post.title}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                  draggable={false}
                />
                <div className="absolute top-4 left-4">
                  <span className="text-[9px] tracking-widest uppercase bg-midnight text-moonlight px-2.5 py-1">
                    {post.tag}
                  </span>
                </div>
              </div>

              {/* Text */}
              <div className="mt-4">
                <h3
                  className="text-lg font-medium text-midnight group-hover:text-midnight/70 transition-colors leading-snug mb-2"
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.15rem" }}
                >
                  {post.title}
                </h3>
                <p className="text-xs text-midnight/50 leading-relaxed line-clamp-2">{post.excerpt}</p>
                <span className="inline-flex items-center gap-1.5 mt-3 text-[10px] tracking-widest uppercase text-midnight/60 group-hover:text-midnight transition-colors">
                  Read More <i className="ri-arrow-right-line text-xs transition-transform group-hover:translate-x-1" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
